import { G } from '@mobily/ts-belt';
import { addDays, endOfMonth, startOfMonth, subDays, subMonths } from 'date-fns';
import { format } from 'date-fns-tz';
import { useCallback, useEffect } from 'react';
import { required, useTranslate, Validator } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { ShowSection } from '@sh4res/bo-common/components';
import { DateInputOnlyYear } from '@sh4res/bo-common/components/DateInputOnlyYear';
import { DateInput } from '@sh4res/bo-common/fields/DateInput';
import { inputDisabledProps } from '@sh4res/bo-common/utils';
import { tryToDateString, tryToLocalDate } from '@sh4res/bo-common/utils/date';

type Props = {
  endFiscalYearDay: number;
  endFiscalYearMonth: number;
  isEditionDisabled: boolean;
  /**
   * Called when the fiscal period start is committed, i.e. once the input loses focus or the
   * value is set programmatically. Consumers can use it to react to the selected fiscal year
   * without reacting to every intermediate keystroke.
   */
  onFiscalPeriodStartCommit?: () => void;
};

const testId = 'BoExternalB2bCampaignCreateReferenceFiscalPeriod';

export const ReferenceFiscalPeriod = (props: Props) => {
  const { endFiscalYearDay, endFiscalYearMonth, isEditionDisabled, onFiscalPeriodStartCommit } =
    props;
  const { trigger, setValue } = useFormContext();
  const translate = useTranslate();
  const reason = useWatch({
    name: 'reason',
  });
  const type = useWatch({
    name: 'type',
  });
  const fiscalPeriodEnd = useWatch({
    name: 'fiscal_period_end',
  });
  const fiscalPeriodStart = useWatch({
    name: 'fiscal_period_start',
  });
  const mustSelectWholeYear = type === 'participation' && reason === 'standard';
  const isEndFreeForm =
    type === 'profit_sharing' ||
    type === 'unused_vacation_days' ||
    type === 'value_sharing_bonus' ||
    type === 'matching_contribution';

  const validateEnd = useCallback<Validator>(
    (_v, allValues) => {
      const value = allValues.fiscal_period_end;
      if (!value) {
        return translate('ra.validation.required');
      }
      if (new Date(value) > new Date()) {
        return translate('resources.bo_common.b2b.campaigns.sections.general.fiscal_period_past');
      }
      if (
        allValues.fiscal_period_start &&
        new Date(value) <= new Date(allValues.fiscal_period_start)
      ) {
        return translate('ra.validation.minValue', {
          min: format(addDays(allValues.fiscal_period_start, 1), 'dd/MM/yyyy'),
        });
      }
    },
    [translate],
  );

  useEffect(() => {
    if (!mustSelectWholeYear || !fiscalPeriodEnd || !endFiscalYearMonth) {
      return;
    }
    const fiscalPeriodEndYear = tryToLocalDate(fiscalPeriodEnd)?.getFullYear();
    if (G.isNullable(fiscalPeriodEndYear)) {
      return;
    }
    // The whole fiscal year starts the day after the previous fiscal year end
    const previousFiscalYearEnd = new Date(
      fiscalPeriodEndYear - 1,
      endFiscalYearMonth - 1,
      endFiscalYearDay,
    );
    setValue('fiscal_period_start', tryToDateString(addDays(previousFiscalYearEnd, 1)), {
      shouldValidate: true,
    });
    trigger('fiscal_period_start');
    trigger('fiscal_period_end');
  }, [
    fiscalPeriodEnd,
    mustSelectWholeYear,
    endFiscalYearMonth,
    endFiscalYearDay,
    setValue,
    trigger,
  ]);

  useEffect(() => {
    if (
      type === 'matching_contribution' &&
      G.isNullable(fiscalPeriodEnd) &&
      G.isNullable(fiscalPeriodStart)
    ) {
      const previousMonthStart = subMonths(startOfMonth(new Date()), 1);
      const previousMonthEnd = endOfMonth(previousMonthStart);

      setValue('fiscal_period_start', tryToDateString(previousMonthStart), {
        shouldValidate: true,
      });
      setValue('fiscal_period_end', tryToDateString(previousMonthEnd), {
        shouldValidate: true,
      });
      onFiscalPeriodStartCommit?.();
    }
  }, [type, fiscalPeriodEnd, fiscalPeriodStart, setValue, onFiscalPeriodStartCommit]);

  return (
    <ShowSection
      leftAligned
      label="resources.bo_common.b2b.campaigns.sections.general.reference_fiscal_period"
      noCard={true}>
      <DateInput
        data-testid={`${testId}_FiscalPeriodStartInput`}
        source="fiscal_period_start"
        isRequired
        validate={[
          required(),
          (value, allValues) => {
            if (
              allValues.fiscal_period_end &&
              new Date(value) >= new Date(allValues.fiscal_period_end)
            ) {
              return translate('ra.validation.maxValue', {
                max: format(subDays(allValues.fiscal_period_end, 1), 'dd/MM/yyyy'),
              });
            }
          },
        ]}
        onChange={async () => trigger('fiscal_period_end')}
        onBlur={() => onFiscalPeriodStartCommit?.()}
        {...inputDisabledProps(isEditionDisabled || mustSelectWholeYear)}
      />
      {isEndFreeForm ? (
        <DateInput
          data-testid={`${testId}_FiscalPeriodEndInput`}
          source="fiscal_period_end"
          isRequired
          validate={[validateEnd]}
          onChange={() => {
            trigger('fiscal_period_start');
          }}
          {...inputDisabledProps(isEditionDisabled)}
        />
      ) : (
        <DateInputOnlyYear
          data-testid={`${testId}_FiscalPeriodEndYearInput`}
          source="fiscal_period_end"
          isRequired
          label="resources.bo_common.b2b.campaigns.fields.fiscal_period_end"
          month={endFiscalYearMonth}
          day={endFiscalYearDay}
          validate={[validateEnd]}
          onChange={() => {
            trigger('fiscal_period_start');
          }}
          {...inputDisabledProps(
            isEditionDisabled || G.isNullable(endFiscalYearMonth) || G.isNullable(endFiscalYearDay),
            {
              '& .MuiSelect-select': {
                backgroundColor: 'transparent !important',
              },
            },
          )}
        />
      )}
    </ShowSection>
  );
};
